import { useCallback, useEffect, useState, useRef } from "react";
import {
  closeAddressFromMap,
  openAddAnotherAddress,
  openSelectAddressModel,
} from "../../store/slices/modalSlice";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Row, Col, Form, Container } from "react-bootstrap";
import { useData } from "../toast/Api";
import { GoogleMap } from "@react-google-maps/api";
import axiosInstance from "../../api/axiosInstance";

const baseUrl = import.meta.env.VITE_API_URL;

interface coordinate {
  lat: number;
  lng: number;
}
interface selectedPlaceData {
  address: string;
  coordinates: coordinate;
  name: string;
}
const BUSINESS_LOCATION = {
  lat: 25.2611948,
  lng: 55.3273586,
};

const AddressFromMap = () => {
  const { showSuccess, showError } = useData();

  const dispatch = useDispatch();
  const [token, setToken] = useState<string | null>();
  const [recordId, setRecordId] = useState("");
  const [addresNickname, setAddresNickname] = useState("");
  const [buildingName, setbuildingName] = useState("");
  const [flat, setFlat] = useState("");
  const [landmark, setLandmark] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [addressType, setAddressType] = useState("billing");
  const [addresNicknameError, setAddresNicknameError] = useState<boolean>(true);

  const [flatError, setFlatError] = useState("");
  const [buildingNameError, setBuildingNameError] = useState("");
  const [landmarkError, setLandmarkError] = useState("");
  const [addresNicknameErrorMsg, setAddresNicknameErrorMsg] = useState("");

  const [selectedAddress, setSelectedAddress] = useState<string | null>(
    "billing"
  );
  const [selectedPlaceData, setSelectedPlaceData] =
    useState<selectedPlaceData>();
  const [center, setCenter] = useState<coordinate>({
    lat: 37.7749,
    lng: -122.4194,
  });

  const showThisModel = useSelector(
    (state: { modal: { addressFromMap: boolean } }) =>
      state.modal.addressFromMap
  );

  const handleMapIdle = useCallback(() => {
    if (!mapRef.current) return;
    
    const newCenter = mapRef.current.getCenter();
    if (!newCenter) return;

    const mapCenter = {
      lat: newCenter.lat(),
      lng: newCenter.lng(),
    };

    setCenter(mapCenter);
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: mapCenter }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setSelectedPlaceData({
          name: results[0].address_components[0].long_name,
          address: cleanAddress(results[0].formatted_address),
          coordinates: mapCenter,
        });
      }
    });
  }, []);

  const mapRef = useRef<google.maps.Map | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const cleanAddress = (address: string) => {
    const parts = address.split(" - ");
    return parts.filter((part) => !/^[A-Za-z0-9+]{2,}$/.test(part)).join(" - ");
  };

  useEffect(() => {
    const selectedPlace = sessionStorage.getItem("selectedPlace");
    if (selectedPlace) {
      const selectPlace = JSON.parse(selectedPlace);
      const newCenter = {
        lat: selectPlace.coordinates.lat,
        lng: selectPlace.coordinates.lng,
      };
      setCenter(newCenter);
      setSelectedPlaceData(selectPlace);
    }
    const tkn = sessionStorage.getItem("tk");
    setToken(tkn);

    const mode = sessionStorage.getItem("mode");

    if (mode === "Edit") {
      const editData = localStorage.getItem("filteredData");

      if (editData) {
        try {
          const parsedData = JSON.parse(editData);
          const {
            RecordId,
            addresNickname,
            address_type,
            building,
            landmark,
            streetName,
            flatNo,
            longitude,
            latitude,
          } = parsedData;
          setAddresNickname(addresNickname);
          setbuildingName(building);
          setFlat(flatNo);
          setLandmark(landmark);
          setIsPrimary(true);
          setAddressType(address_type);
          setRecordId(RecordId);
          if (!selectedPlace) {
            const newCenter = {
              lat: latitude,
              lng: longitude,
            };
            setSelectedPlaceData({
              address: streetName,
              coordinates: newCenter,
              name: streetName,
            });
            setCenter(newCenter);
          }
        } catch (error) {
          console.error("Error parsing JSON from localStorage:", error);
        }
      } else {
      }
    }
  }, []);

  const handleSelectAddress = (address: string) => {
    if (address === "shipping") {
      setAddresNickname("Home");
    } else if (address === "billing") {
      setAddresNickname("Office");
    }

    setSelectedAddress(address);
  };

  const handleInputChange = (value: string) => {
    setAddresNickname(value);

    const namereg = /^[A-Za-z ]+$/;
    const trimmedValue = value.trim();

    const isInvalid = trimmedValue !== "" && namereg.test(trimmedValue);
    setAddresNicknameError(isInvalid);
  };

  const openSelectAddress = async () => {
    let hasError = false;

    if (!flat) {
      setFlatError("Please enter your house/flat number.");
      hasError = true;
    } else {
      setFlatError("");
    }

    if (!buildingName) {
      setBuildingNameError("Please enter your building/street address.");
      hasError = true;
    } else {
      setBuildingNameError("");
    }

    if (!landmark) {
      setLandmarkError("Please enter a landmark.");
      hasError = true;
    } else {
      setLandmarkError("");
    }

    if (!addresNickname || !addresNicknameError) {
      setAddresNicknameErrorMsg("Please enter a valid name for this location.");
      hasError = true;
    } else {
      setAddresNicknameErrorMsg("");
    }

    if (hasError) {
      showError("Please fill all the fields correctly.");
      return;
    }

    const mode = sessionStorage.getItem("mode");
    if (
      buildingName != "" &&
      flat != "" &&
      landmark != "" &&
      addresNickname != ""
    ) {
      if (mode === "Edit") {
        try {
          const data = {
            id: recordId,
            address: selectedAddress,
            address_type: addressType,
            addresNickname: addresNickname,
            streetName: selectedPlaceData?.address,
            building: buildingName,
            floor: "floor",
            flatNo: flat,
            landmark: landmark,
            primaryAddress: isPrimary,
            longitude: center.lng,
            latitude: center.lat,
          };

          if (!baseUrl) {
            throw new Error(
              "Base URL is not defined in the environment variables"
            );
          }

          const response = await axiosInstance.put(
            `/api/user/userAddressUpdate?id=${recordId}`,
            data
          );

          if (response) {
            sessionStorage.setItem(
              "billing",
              JSON.stringify(Math.floor(Math.random() * 1000) + 1)
            );
            showSuccess(" Address Successfully Updated");
            sessionStorage.removeItem("mode");
            localStorage.removeItem("filteredData");
            sessionStorage.removeItem("selectedPlace");
            dispatch(closeAddressFromMap());
            dispatch(openSelectAddressModel());
          }
        } catch (error) {
        }
      } else {
        try {
          const data = {
            address_type: addressType,
            addresNickname: addresNickname,
            streetName: selectedPlaceData?.address,
            building: buildingName,
            floor: flat,
            flatNo: flat,
            landmark: landmark,
            primaryAddress: isPrimary,
            longitude: center.lng,
            latitude: center.lat,
          };

          if (!baseUrl) {
            throw new Error(
              "Base URL is not defined in the environment variables"
            );
          }

          const response = await axiosInstance.post(
            `${baseUrl}/api/user/userAddressAdd`,
            data,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response) {
            sessionStorage.setItem(
              "billing",
              JSON.stringify(Math.floor(Math.random() * 1000) + 1)
            );

            showSuccess("Address Saved");
            sessionStorage.removeItem("selectedPlace");
            dispatch(closeAddressFromMap());
            dispatch(openSelectAddressModel());
          }
        } catch (error) {
          console.error("Error in addUserAddress:", error);
        }
      }
    } else {
      showError("pllz fill all the fields");
    }
  };

  const closeThisModel = () => {
    sessionStorage.removeItem("selectedPlace");
    sessionStorage.removeItem("mode");
    localStorage.removeItem("filteredData");
    dispatch(closeAddressFromMap());
  };

  const handleChange = () => {
    dispatch(closeAddressFromMap());
    dispatch(openAddAnotherAddress());
  };
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCenter(userLocation);
          if (mapRef.current) {
            mapRef.current.panTo(userLocation);
          }
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: userLocation }, (results, status) => {
            if (status === "OK" && results?.[0]) {
              setSelectedPlaceData({
                name: results[0].address_components[0].long_name,
                address: cleanAddress(results[0].formatted_address),
                coordinates: userLocation,
              });
            }
          });
        },
        () => {
          alert("Please Enable Location Permission");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };
  const handleBusinessLocation = () => {
    setCenter(BUSINESS_LOCATION);
    if (mapRef.current) {
      mapRef.current.panTo(BUSINESS_LOCATION);
      mapRef.current.setZoom(19);
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: BUSINESS_LOCATION }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setSelectedPlaceData({
          name: results[0].address_components[0].long_name,
          address: cleanAddress(results[0].formatted_address),
          coordinates: BUSINESS_LOCATION,
        });
      }
    });
  };

  return (
    <>
      <Modal
        show={showThisModel}
        onHide={closeThisModel}
        centered
        size="lg"
        className="mb-fix slide-from-bottom"
        backdrop="static"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body className="p-0 rounded p-3">
          <Row>
            <p className="font-17 ff-semibold text-black">
              Please fill address information
            </p>
            <Col md={6}>
              <div className="rounded-lg rounded-top overflow-hidden position-relative">
                <GoogleMap
                  mapContainerClassName="map-container"
                  zoom={13}
                  center={center}
                  onIdle={handleMapIdle}
                  onLoad={onMapLoad}
                  options={{
                    zoomControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy",
                  }}
                />
                <div 
                  className="position-absolute" 
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1
                  }}
                >
                  <img 
                    src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/map-picker.svg"
                    alt="Location marker"
                    style={{ width: '48px', height: '48px' }}
                  />
                </div>
                {/* Business location button */}
                <button
                  onClick={handleBusinessLocation}
                  style={{
                    position: 'absolute',
                    bottom: 72, // 16px (GPS) + 40px (button) + 16px (gap)
                    left: 16,
                    background: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    cursor: 'pointer',
                    padding: 0
                  }}
                  title="Go to business location"
                >
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/684/684908.png"
                    alt="Business location"
                    style={{ width: 24, height: 24 }}
                  />
                </button>
                {/* GPS button at bottom-left */}
                <button
                  onClick={handleUseCurrentLocation}
                  style={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    background: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    cursor: 'pointer',
                    padding: 0
                  }}
                  title="Use current location"
                >
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/61/61168.png"
                    alt="Current location"
                    style={{ width: 24, height: 24 }}
                  />
                </button>
              </div>
            </Col>
            <Col md={6} className="ps-0 custom-modal-section">
              <div className="modal-content-wrapper">
                <div className="modal-content-scroll p-0 px-2">
                  <div>
                    <div
                      className="d-flex justify-content-start align-items-center p-2 bg-3 cursor-pointer rounded-3"
                      onClick={handleChange}
                      style={{ width: "fit-content" }}
                    >
                      <div>
                        <img
                          src="https://goodhand.b-cdn.net/Assets/cart/cart-flow/map-new.svg"
                          alt=""
                          className="me-2"
                        />
                      </div>
                      <div className="font-14 text-main text-decoration-underline ff-medium">
                        Change location
                      </div>
                    </div>

                    <div className="details-loc my-3 border-bottom pt-2 border-top">
                      <p className="font-14 ff-medium text-black-20 mb-2 address-map-modal">
                        {selectedPlaceData?.address || `Lat: ${center.lat.toFixed(6)}, Lng: ${center.lng.toFixed(6)}`}
                      </p>
                    </div>

                    <Form.Group className="mb-3 form-floating-custom">
                      <Form.Control
                        type="text"
                        id="House"
                        className={`form-control py-3 ${flat ? "filled" : ""} ${flatError ? "is-invalid" : ""}`}
                        value={flat}
                        onChange={(e) => setFlat(e.target.value)}
                      />
                      <Form.Label htmlFor="House">House Flat Number</Form.Label>
                      {flatError && <div className="invalid-feedback d-block">{flatError}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3 form-floating-custom">
                      <Form.Control
                        type="text"
                        id="Building"
                        className={`font-12 py-3 ${buildingName ? "filled" : ""} ${buildingNameError ? "is-invalid" : ""}`}
                        value={buildingName}
                        onChange={(e) => setbuildingName(e.target.value)}
                 
                      />
                      <Form.Label htmlFor="Building">
                        Building Name, Street Address
                      </Form.Label>
                      {buildingNameError && <div className="invalid-feedback d-block">{buildingNameError}</div>}
                    </Form.Group>
                    <Form.Group className="mb-3 form-floating-custom">
                      <Form.Control
                        type="text"
                        id="landmark"
                        className={`font-12 py-3 ${landmark ? "filled" : ""} ${landmarkError ? "is-invalid" : ""}`}
                        value={landmark}
                        onChange={(e) => setLandmark(e.target.value)}
                      />
                      <Form.Label htmlFor="landmark">Enter Landmark</Form.Label>
                      {landmarkError && <div className="invalid-feedback d-block">{landmarkError}</div>}
                    </Form.Group>

                    <Container className="p-0">
                      <div className="d-flex align-items-center gap-2  my-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="addressType"
                            id="radioHome"
                            value="shipping"
                            checked={selectedAddress === "shipping"}
                            onChange={() => handleSelectAddress("shipping")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="radioHome"
                          >
                            Home
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="addressType"
                            id="radioOther"
                            value="billing"
                            checked={selectedAddress === "billing"}
                            onChange={() => handleSelectAddress("billing")}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="radioOther"
                          >
                            Other
                          </label>
                        </div>
                      </div>
                    </Container>

                    {selectedAddress === "billing" && (
                      <>
                    
                    <Form.Group className="mb-3 form-floating-custom">
                      <Form.Control
                        type="text"
                        id="locationName"
                        className={`font-12 py-3 ${addresNickname ? "filled" : ""} ${addresNicknameErrorMsg ? "is-invalid" : ""}`}
                        value={addresNickname}
                        onChange={(e) => handleInputChange(e.target.value)}
                      />
                      <Form.Label htmlFor="landmark">Other</Form.Label>
                      {addresNicknameErrorMsg && <div className="invalid-feedback d-block">{addresNicknameErrorMsg}</div>}
                    </Form.Group>

                        {/* <Form.Group className="mb-3 form-floating-custom">
                          <Form.Control
                            type="text"
                            id="locationName"
                            className="font-12 py-3"
                            value={addresNickname}
                            onChange={(e) => handleInputChange(e.target.value)}
                            isInvalid={!addresNicknameError}
                          /> <Form.Label htmlFor="landmark">Enter Landmark</Form.Label>
                          <Form.Control.Feedback type="invalid">
                            Oops! You forgot to enter a name for this location.
                          </Form.Control.Feedback>
                        </Form.Group> */}
                      </>
                    )}
                  </div>
                </div>
                <Button className="save-button" onClick={openSelectAddress}>
                  Save
                </Button>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddressFromMap;
